import {
  createContext,
  useLayoutEffect,
  useState,
  useContext,
  useEffect,
} from "react";
import { axiosInstance } from "../api/axiosInstance";
import { isAxiosError } from "axios";
import { safeJsonParse } from "../utils/api-utils";
import { PageLoader } from "../components/loaders";
import { Mixpanel } from "../utils/mixpanel";

export const AuthenticationContext = createContext({});

export const AuthenticationProvider = ({ children }) => {
  const getStoredItem = (key) => {
    try {
      const item = localStorage.getItem(key);
      return key === "user" ? safeJsonParse(item) : item;
    } catch {
      return null;
    }
  };

  const updateStoredItem = (key, value) => {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      const storageValue = key === "user" ? JSON.stringify(value) : value;
      localStorage.setItem(key, storageValue);
    }
  };

  //  DRY state initialization
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() =>
    getStoredItem("accessToken")
  );
  const [refreshToken, setRefreshToken] = useState(() =>
    getStoredItem("refreshToken")
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [isAppInitialized, setIsAppInitialized] = useState(false);

  const isAuthenticated = !!accessToken;

  //  DRY token management
  const updateTokens = (newAccessToken, newRefreshToken) => {
    updateStoredItem("accessToken", newAccessToken);
    updateStoredItem("refreshToken", newRefreshToken);

    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
  };
  const clearTokens = () => {
    updateTokens(null, null);
    setUser(null);
  };

  // Request interceptor
  useLayoutEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        if (accessToken) {
          config.headers["x-authenticated-user"] = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const msg = error.response?.data?.message?.toLowerCase();
        const isExpiredToken =
          msg.includes("x-authenticated-user") || msg.includes("unauthorized");
        if (error.response?.status === 401 && refreshToken && isExpiredToken) {
          try {
            const res = await axiosInstance.post("/auth/token/refresh", {
              token: refreshToken,
            });
            const newAccess = res.data.access_token;
            updateTokens(newAccess, refreshToken);
            error.config.headers[
              "x-authenticated-user"
            ] = `Bearer ${newAccess}`;
            return axiosInstance(error.config);
          } catch (err) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [accessToken, refreshToken, user]);

  useEffect(() => {
    if (isAuthenticated && isGuestUser) {
      sessionStorage.removeItem("guest_user");
      setIsGuestUser(false);
    }
  }, [isAuthenticated]);

  // Initialize app
  useLayoutEffect(() => {
    const initializeApp = async () => {
      try {
        await initializeAuth();
      } finally {
        setIsAppInitialized(true);
      }
    };
    initializeApp();
  }, []);

  // Check if guest user exists or not (if exists set guest user)
  useLayoutEffect(() => {
    const storedGuest = sessionStorage.getItem("guest_user");
    if (storedGuest && !accessToken) {
      setUser(JSON.parse(storedGuest));
      setIsGuestUser(true);
    }
  }, []);

  //  DRY API request handler
  const makeApiRequest = async (apiCall, runOnClose) => {
    setIsLoading(true);
    try {
      const result = await apiCall();
      const data = result.data;
      return {
        success: data.status || true,
        data,
        message:
          typeof data.message === "object" ? data.message[0] : data.message,
      };
    } catch (err) {
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
          err.response?.data?.message || err.message || "Something went wrong",
      };
    } finally {
      setIsLoading(false);
      runOnClose?.();
    }
  };

  //  Login user
  const createGuestUser = async (credentials, runOnClose) => {
    return makeApiRequest(async () => {
      const { data: respData } = await axiosInstance.post(
        "/create_guest_user",
        credentials,
        {
          headers: {
            "guest-user-key": process.env.REACT_APP_API_GUEST_USER_KEY,
          },
        }
      );
      const guest = respData?.data;
      if (guest) {
        setUser(respData?.data);
        setIsGuestUser(true);
        sessionStorage.setItem("guest_user", JSON.stringify(guest));
        Mixpanel.identify_guest({
          ...credentials,
          guest,
        });
      }
      return { data: respData };
    }, runOnClose);
  };

  const login = async (credentials, runOnClose) => {
    sessionStorage.removeItem("guest_user");
    setIsGuestUser(false);
    return makeApiRequest(async () => {
      const { data } = await axiosInstance.post("/auth/login", credentials);
      const {
        access_token: accessToken,
        refresh_token: refreshToken,
        userid: user,
      } = data;

      updateTokens(accessToken, refreshToken);

      const userInfo = await fetchUserInfo();
      if (userInfo) {
        setUser(userInfo.data);
        Mixpanel.identify_user({
          id: userInfo.data.id || userInfo.data._id,
          email: userInfo.data.email,
          phone: userInfo.data.phone || userInfo.data.phonenumber,
          firstName: userInfo.data.firstname,
          lastName: userInfo.data.lastname,
          createdAt: userInfo.data.createdAt || userInfo.data.signup_date,
        });
      }
      return { data };
    }, runOnClose);
  };

  const logout = () => {
    Mixpanel.track("User Logged Out");
    Mixpanel.reset();

    clearTokens();
    sessionStorage.removeItem("guest_user");
    setIsGuestUser(false);
    setUser(null);
  };

  const initializeAuth = async () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const userInfo = await fetchUserInfo();
        if (userInfo) {
          setUser(userInfo.data);
          Mixpanel.identify_user({
            id: userInfo.data.id || userInfo.data._id,
            email: userInfo.data.email,
            phone: userInfo.data.phone || userInfo.data.phonenumber,
            firstName: userInfo.data.firstname || userInfo.data.firstName,
            lastName: userInfo.data.lastname || userInfo.data.lastName,
            createdAt: userInfo.data.createdAt || userInfo.data.signup_date,
          });
          Mixpanel.track("Session Restored", {
            user_id: userInfo.data.id,
          });
          return true;
        }
      } catch {
        logout();
      }
    } else {
      const storedGuest = sessionStorage.getItem("guest_user");
      if (storedGuest) {
        const guestData = JSON.parse(storedGuest);
        setUser(guestData);
        setIsGuestUser(true);
        Mixpanel.identify_guest(guestData);
      }
    }
    return false;
  };

  const fetchUserInfo = async () => {
      const currentToken = localStorage.getItem("accessToken");

      try {
      const { data } = await axiosInstance.get("/profile", {
        headers: {
          "x-authenticated-user": `Bearer ${currentToken}`,
        },
      });
      return data;
    } catch (err) {
      console.error("Failed to fetch user info:", err);
      return null;
    }
  };

  const apiEndpoints = {
    register: (formInfo) => axiosInstance.post("/auth/register", formInfo),
    activateAccount: (formInfo) =>
      axiosInstance.post("/auth/activate_account", formInfo),
    resendOtp: (formInfo) => axiosInstance.post("/auth/resend_otp", formInfo),
    forgotPassword: (formInfo) =>
      axiosInstance.post("/auth/forgot_password", formInfo),
    resetPassword: (formInfo) =>
      axiosInstance.post("/auth/reset_password", formInfo),
  };

  const createApiMethod = (endpoint) => (formInfo, runOnClose) =>
    makeApiRequest(() => apiEndpoints[endpoint](formInfo), runOnClose);

  const register = createApiMethod("register");
  const activateAccount = createApiMethod("activateAccount");
  const resendOtp = createApiMethod("resendOtp");
  const forgotPassword = createApiMethod("forgotPassword");
  const resetPassword = createApiMethod("resetPassword");

  if (!isAppInitialized) return <PageLoader />;

  return (
    <AuthenticationContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        isGuestUser,
        isAppInitialized,
        user,
        login,
        logout,
        register,
        activateAccount,
        resendOtp,
        forgotPassword,
        resetPassword,
        fetchUserInfo,
        setUser,
        initializeAuth,
        createGuestUser,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = () => useContext(AuthenticationContext);
